import { NutritionProduct } from "../services/NutritionProductsService";
import { Plan, SectionData } from "../services/PlansService";
import { getCountPerSurface, getCountPerTechnicalityType } from "./getSurfaceData";
import { getProductList, getTotalSectionNutrition } from "./NutritionUtils";
import { round } from "./UnitsUtils";

export type RunnerData = {
    experience_level: "beginner" | "intermediate" | "advanced";
    goals: {
        primary: string;
        secondary: string;
    };
    training_background: {
        avg_weekly_km: number;
        avg_weekly_vert_m: number;
        longest_run_km: number;
    };
    units_for_response: {
        distance: "km" | "miles";
        elevation: "meters" | "feet";
    };
};

const technicalityMap = [
    "easy",
    "mostly easy",
    "moderate",
    "technical",
    "very technical",
    "extremely technical",
    "alpine"
];

const getPromptJson = (plan: Plan, sectionsData: SectionData[], runner: RunnerData, nutritionProducts: NutritionProduct[], query: "initial" | "full" = "full") => {

    const surfaceCounts = getCountPerSurface(plan.surfaceData || []);
    const technicalityCounts = getCountPerTechnicalityType(plan.surfaceData || []);
    const surfaceTotal = surfaceCounts.reduce((total, { value }) => total + value, 0);
    const technicalityTotal = technicalityCounts.reduce((total, { value }) => total + value, 0);

    const controlPointNames = plan.controlPoints.map((cp, index) => cp.type === "start" ? "Start" : cp.type === "end" ? "Finish" : cp.name || `Control Point ${Math.ceil(index / 2)}`);

    const productList = getProductList(plan.plannedNutrition, nutritionProducts);
    return {
        race: {
            name: plan.name,
            total_distance_km: plan.distance,
            total_elevation_gain_m: plan.elevationGain,
            cutoffs: [],
            terrain_summary: {
                surfaces: surfaceCounts.map(({ id, value }) => value === 0 ? "" : `${id} ${Math.round((value / surfaceTotal) * 100)} %`).filter(Boolean).join(", "),
                technicality: technicalityCounts.map(({ id, value }) => value === 0 ? "" : `${+id >= 0 ? technicalityMap[+id] : "unknown"} ${Math.round((value / technicalityTotal) * 100)} %`).filter(Boolean).join(", ")
            },
            start_time: plan.date.toISOString(),
        },

        runner,

        sections: sectionsData.map((section, index) => {

            const sectionNutritionValues = getTotalSectionNutrition(plan.plannedNutrition[index]?.products || [], nutritionProducts);

            if (section.type !== "section") {
                return {
                    section_number: index + 1,
                    name: section.name || (section.type === "start" ? "Start" : section.type === "end" ? "Finish" : controlPointNames[Math.ceil(index / 2)]),
                    type: section.type === "control" ? "aid station" : section.type,
                    found_at_distance_km: section.distance || 0,
                    user_expected_break_time_in_minutes: query === "full" ? plan.plannedEffort[index]?.time : undefined,
                    user_nutrition_strategy_products: query === "full" ? plan.plannedNutrition[index]?.products?.filter(p => p.productId !== "empty" && p.quantity > 0).map(product => `${product.quantity} x ${nutritionProducts.find(p => product.productId === "water" ? "Liters of water" : p.id === product.productId)?.name || "Unknown product"}`) : undefined,
                    user_notes: plan.notes ? plan.notes[index] : undefined,
                    user_nutrition_strategy_for_break: query === "full" ? {
                        water_liters: sectionNutritionValues.water,
                        calories_kcal: sectionNutritionValues.calories,
                        protein_g: sectionNutritionValues.protein,
                        carbs_g: sectionNutritionValues.carbs,
                        fat_g: sectionNutritionValues.fat,
                        caffeine_mg: sectionNutritionValues.caffeine,
                        sodium_mg: sectionNutritionValues.sodium
                    } : undefined
                }
            }

            const surfaceDataOfSection = plan.surfaceData?.filter(surface => (surface.distance || 0) >= section.startDistance && (surface.distance || 0) <= section.endDistance);
            const surfaceCounts = getCountPerSurface(surfaceDataOfSection || []);
            const technicalityCounts = getCountPerTechnicalityType(surfaceDataOfSection || []);
            const surfaceTotal = surfaceCounts.reduce((total, { value }) => total + value, 0);
            const technicalityTotal = technicalityCounts.reduce((total, { value }) => total + value, 0);
            const name = `${controlPointNames[Math.ceil(index / 2)-1]} → ${controlPointNames[Math.ceil(index / 2)]}`;

            return {
                section_number: index + 1,
                section_type: "regular",
                name,
                distance_km: round(section.endDistance - section.startDistance, 2) / 1000,
                elevation_gain_m: Math.max(0, round(section.elevationGain || 0, 0)),
                elevation_loss_m: Math.min(0, round(section.elevationLoss || 0, 0)),
                max_altitude_m: round(section.maxElevation || 0, 0),
                min_altitude_m: round(section.minElevation || 0, 0),
                terrain_summary: {
                    surfaces: surfaceCounts.map(({ id, value }) => `${id} ${Math.round((value / surfaceTotal) * 100)} %`).join(", "),
                    technicality: technicalityCounts.map(({ id, value }) => `${+id > 0 ? technicalityMap[+id] : "unknown"} ${Math.round((value / technicalityTotal) * 100)} %`).join(", ")
                },
                user_expected_time_in_minutes: query === "full" ? plan.plannedEffort[index]?.time : undefined,
                user_notes: plan.notes ? plan.notes[index] : undefined,
                user_nutrition_strategy_products: query === "full" ? plan.plannedNutrition[index]?.products?.filter(p => !!p.productId && p.quantity > 0).map(product => `${product.quantity} x ${nutritionProducts.find(p => product.productId === "water" ? "Liters of water" : p.id === product.productId)?.name || "Unknown product"}`) : undefined,
                user_nutrition_strategy_for_section: query === "full" ? {
                    water_liters: sectionNutritionValues.water,
                    calories_kcal: sectionNutritionValues.calories,
                    protein_g: sectionNutritionValues.protein,
                    carbs_g: sectionNutritionValues.carbs,
                    fat_g: sectionNutritionValues.fat,
                    caffeine_mg: sectionNutritionValues.caffeine,
                    sodium_mg: sectionNutritionValues.sodium
                } : undefined
            };
        }),

        user_plan: query === "full" ? {
            total_expected_time: plan.plannedEffort.reduce((total, effort) => total + (effort.time || 0), 0),
            overall_nutrition_strategy: {
                products: productList.map(product => `${product.quantity} x ${product.name}`),
                totals: {
                    water_liters: round(plan.plannedNutrition.reduce((total, nutrition) => total + (nutrition.products?.find(p => p.productId === "water")?.quantity || 0), 0), 2),
                    calories_kcal: plan.plannedNutrition.reduce((total, nutrition) => total + getTotalSectionNutrition(nutrition.products || [], nutritionProducts).calories, 0),
                    protein_g: plan.plannedNutrition.reduce((total, nutrition) => total + getTotalSectionNutrition(nutrition.products || [], nutritionProducts).protein, 0),
                    carbs_g: plan.plannedNutrition.reduce((total, nutrition) => total + getTotalSectionNutrition(nutrition.products || [], nutritionProducts).carbs, 0),
                    fat_g: plan.plannedNutrition.reduce((total, nutrition) => total + getTotalSectionNutrition(nutrition.products || [], nutritionProducts).fat, 0),
                    caffeine_mg: plan.plannedNutrition.reduce((total, nutrition) => total + getTotalSectionNutrition(nutrition.products || [], nutritionProducts).caffeine, 0),
                    sodium_mg: plan.plannedNutrition.reduce((total, nutrition) => total + getTotalSectionNutrition(nutrition.products || [], nutritionProducts).sodium, 0)
                }
            },
        } : undefined
    }

};

export default getPromptJson;